import { useState, useEffect } from "react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Globe, Loader2 } from "lucide-react";

const LANG_CHOICES = ["English", "Hindi", "Spanish", "French", "German", "Arabic", "Chinese (Simplified)", "Portuguese", "Russian", "Japanese", "Bengali", "Tamil", "Urdu", "Indonesian", "Turkish"];

export default function LanguageSwitcher() {
  const { language, setLanguage, translating } = useI18n();
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [choices, setChoices] = useState(LANG_CHOICES);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/meta").then((r) => {
      const langs = r.data.languages || LANG_CHOICES;
      setChoices(Array.from(new Set([language, user?.ui_language, ...langs].filter(Boolean))));
    }).catch(() => {});
  }, [user]); // eslint-disable-line
  const pick = async (lang) => {
    setOpen(false);
    setQ("");
    await setLanguage(lang);
    try { await api.put("/profile", { ui_language: lang }); await refresh(); } catch {}
  };
  const filtered = choices.filter((l) => l.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative">
      <button data-testid="language-switcher" onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl hover:bg-blue-50 text-slate-600 text-sm">
        {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
        <span className="hidden sm:inline max-w-[90px] truncate" data-notranslate>{language}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 rounded-xl border border-slate-200 bg-white shadow-lg z-50 overflow-hidden">
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
            <input
              data-testid="language-search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search language…"
              className="w-full px-2.5 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30"
            />
          </div>
          <div className="max-h-64 overflow-auto">
            {filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No matches</p>}
            {filtered.map((l) => (
              <button key={l} data-testid={`lang-option-${l}`} onClick={() => pick(l)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#EBF3FB] ${l === language ? "text-[#1E5AA8] font-semibold" : "text-slate-600"}`} data-notranslate>{l}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
